import { useTolgee } from '@tolgee/react';
import { useRouter } from 'next/router';
import { useEffect } from 'react';

import { localeOptions } from '../providers/tolgee';
import useAccessBadge from './useAccessBadge';

export function useLocale() {
  const tolgee = useTolgee();
  const router = useRouter();
  const { accessBadge, isFetched, isSuccess, isFetching } = useAccessBadge();
  const preferredLocale = accessBadge?.user.preferredLocale ?? getPreferredLocale();
  // Check if we're on a localized URL (e.g., /lang/es-ES or /lang/es-ES/pricing)
  const isLocalizedPage = router.pathname.startsWith('/lang/[locale]');
  const urlLocale = isLocalizedPage ? (router.query.locale as string) : null;
  const browserLanguage = getBrowserLanguage();

  const targetLocale = [preferredLocale, urlLocale, browserLanguage]
    .filter((l): l is string => !!l)
    .map(getAvailableLocaleFromCode)
    .filter((l): l is string => !!l)
    .shift();

  useEffect(() => {
    const currentLanguage = tolgee.getLanguage();
    if (
      ((isFetched && !isFetching) || (!isSuccess && !isFetching)) &&
      targetLocale &&
      targetLocale !== currentLanguage
    ) {
      tolgee.changeLanguage(targetLocale);
      storePreferredLocale(targetLocale);
    }
  }, [isFetched, isFetching, isSuccess, targetLocale, tolgee]);
}

export function usePreferredLocale() {
  const { accessBadge, isFetching } = useAccessBadge();
  const preferredLocale = accessBadge?.user.preferredLocale ?? getPreferredLocale();
  return { preferredLocale, isFetched: !isFetching };
}

export function getPreferredLocale() {
  if (typeof window !== 'undefined') {
    return localStorage.getItem('curipod.preferredLocale');
  }
  return null;
}

export function storePreferredLocale(locale: string) {
  // Check if we're on the client side
  if (typeof window !== 'undefined') {
    localStorage.setItem('curipod.preferredLocale', locale);
  }
}

export function getBrowserLanguage() {
  // Check if we're on the server side
  if (typeof window === 'undefined') {
    return undefined;
  }
  if (navigator.languages !== undefined) return navigator.languages[0];
  return navigator.language;
}

export function getAvailableLocaleFromCode(code: string): undefined | string {
  const lowerCasedCode = code.toLowerCase();
  const matchingLocale = localeOptions.find(
    ([value]) =>
      value.toLowerCase().startsWith(lowerCasedCode) ||
      lowerCasedCode.startsWith(value.toLowerCase()),
  )?.[0];
  if (matchingLocale) {
    return matchingLocale;
  }

  if (lowerCasedCode.length > 2) {
    return localeOptions.find(([value]) =>
      value.toLowerCase().startsWith(lowerCasedCode.slice(0, 2)),
    )?.[0];
  }
  return undefined;
}
